<template>
  <v-menu
    offset-y
    left
    :close-on-content-click="false"
    max-width="400"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        outlined
        class="text-none"
        v-bind="attrs"
        v-on="on"
      >
        <font-awesome-icon
          :icon="['fas', 'bookmark']"
          size="sm"
          class="mr-2"
        />
        Saved Filters
        <v-badge
          v-if="viewFilters.length > 0"
          :content="viewFilters.length"
          color="primary"
          overlap
          offset-x="10"
          offset-y="10"
          inline
          class="ml-2"
        />
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="py-2 px-3 d-flex align-center">
        <font-awesome-icon
          :icon="['fas', 'bookmark']"
          class="mr-2"
          size="sm"
        />
        <span class="text-subtitle-2">Saved Filters</span>
        <v-spacer />
        <v-btn
          icon
          x-small
          @click="$emit('save-current')"
        >
          <font-awesome-icon
            :icon="['fas', 'plus']"
            size="sm"
          />
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text
        v-if="viewFilters.length === 0"
        class="py-6 text-center grey--text"
      >
        <font-awesome-icon
          :icon="['far', 'bookmark']"
          size="2x"
          class="mb-2"
        />
        <div class="text-caption">
          No saved filters yet
        </div>
        <v-btn
          text
          x-small
          color="primary"
          class="mt-2"
          @click="$emit('save-current')"
        >
          Save your first filter
        </v-btn>
      </v-card-text>

      <v-list
        v-else
        dense
        class="py-0"
        max-height="400"
        style="overflow-y: auto;"
      >
        <v-list-item
          v-for="filter in viewFilters"
          :key="filter.id"
          @click="applyFilter(filter)"
        >
          <v-list-item-content>
            <v-list-item-title class="d-flex align-center">
              <font-awesome-icon
                :icon="['fas', 'filter']"
                size="xs"
                class="mr-2 grey--text"
              />
              <span>{{ filter.name }}</span>
              <v-chip
                v-if="isActiveFilter(filter.id)"
                x-small
                color="primary"
                class="ml-2"
              >
                Active
              </v-chip>
            </v-list-item-title>
            <v-list-item-subtitle class="text-caption mt-1">
              {{ getFilterSummary(filter) }}
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action class="ma-0 ml-2">
            <div class="d-flex">
              <v-menu
                offset-y
                left
                :close-on-content-click="false"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    class="mr-1"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'ellipsis-vertical']"
                      size="xs"
                    />
                  </v-btn>
                </template>

                <v-list dense>
                  <v-list-item @click="renameFilter(filter)">
                    <v-list-item-icon class="mr-2">
                      <font-awesome-icon
                        :icon="['fas', 'pen']"
                        size="xs"
                      />
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="text-caption">
                        Rename
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item @click="confirmUpdate(filter)">
                    <v-list-item-icon class="mr-2">
                      <font-awesome-icon
                        :icon="['fas', 'rotate']"
                        size="xs"
                      />
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="text-caption">
                        Update with Current
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item @click="duplicateFilter(filter)">
                    <v-list-item-icon class="mr-2">
                      <font-awesome-icon
                        :icon="['fas', 'copy']"
                        size="xs"
                      />
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="text-caption">
                        Duplicate
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-divider class="my-1" />

                  <v-list-item @click="confirmDelete(filter)">
                    <v-list-item-icon class="mr-2">
                      <font-awesome-icon
                        :icon="['fas', 'trash']"
                        size="xs"
                        class="error--text"
                      />
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="text-caption error--text">
                        Delete
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-card>

    <!-- Delete Confirmation Dialog -->
    <v-dialog
      v-model="deleteDialog"
      max-width="400"
    >
      <v-card>
        <v-card-title class="text-h6">
          Delete Filter?
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete "<strong>{{ filterToDelete?.name }}</strong>"?
          This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="deleteDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            text
            :loading="deleting"
            @click="deleteFilter"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Update Confirmation Dialog -->
    <v-dialog
      v-model="updateDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title class="text-h6">
          Update Filter with Current State?
        </v-card-title>
        <v-card-text>
          <p class="mb-2">
            Replace "<strong>{{ filterToUpdate?.name }}</strong>" with your current filter settings?
          </p>
          <v-alert
            type="info"
            text
            dense
            class="mb-0"
          >
            This will overwrite the saved filter values with what's currently active in the view.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="updateDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            text
            :loading="updating"
            @click="updateFilter"
          >
            Update Filter
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-menu>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const { mapGetters: mapSavedFiltersGetters } = createNamespacedHelpers('savedFilters')

export default {
  name: 'SavedFiltersMenu',

  props: {
    viewName: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      deleteDialog: false,
      filterToDelete: null,
      deleting: false,
      updateDialog: false,
      filterToUpdate: null,
      updating: false,
    }
  },

  computed: {
    ...mapSavedFiltersGetters(['filtersByView', 'activeFilter']),

    viewFilters() {
      return this.filtersByView(this.viewName)
    },
  },

  methods: {
    isActiveFilter(filterId) {
      return this.activeFilter?.id === filterId
    },

    getFilterSummary(filter) {
      const parts = []
      const { filters } = filter

      if (!filters) return 'No filters'

      // Count active filter types
      if (Array.isArray(filters.selectedPersonFilter) && filters.selectedPersonFilter.length > 0) {
        parts.push(`${filters.selectedPersonFilter.length} person(s)`)
      }
      if (filters.selectedTimeTypeFilter) {
        parts.push(filters.selectedTimeTypeFilter)
      }
      if (filters.selectedClientFilter) {
        parts.push(filters.selectedClientFilter)
      }
      if (filters.selectedProjectFilter) {
        parts.push('Project')
      }
      if (filters.tableSearch) {
        parts.push('Search')
      }

      return parts.length > 0 ? parts.join(' • ') : 'No active filters'
    },

    async applyFilter(filter) {
      try {
        // Set as active filter
        await this.$store.dispatch('savedFilters/setActiveFilter', filter.id)

        // Emit event to parent to apply the filter values
        this.$emit('apply-filter', filter)

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `Applied filter: ${filter.name}`,
          status: 'success',
        }, { root: true })
      } catch (error) {
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `Error applying filter: ${error.message}`,
          status: 'error',
        }, { root: true })
      }
    },

    renameFilter(filter) {
      // Emit event to parent to handle renaming (only name, preserve filter values)
      this.$emit('rename-filter', filter)
    },

    confirmUpdate(filter) {
      this.filterToUpdate = filter
      this.updateDialog = true
    },

    async updateFilter() {
      if (!this.filterToUpdate) return

      this.updating = true

      try {
        // Emit event to parent to get current filter state
        this.$emit('update-filter', this.filterToUpdate)
        this.updateDialog = false
        this.filterToUpdate = null
      } catch (error) {
        // Error handled by parent
      } finally {
        this.updating = false
      }
    },

    duplicateFilter(filter) {
      // Emit event to parent to handle duplication
      this.$emit('duplicate-filter', filter)
    },

    confirmDelete(filter) {
      this.filterToDelete = filter
      this.deleteDialog = true
    },

    async deleteFilter() {
      if (!this.filterToDelete) return

      this.deleting = true

      try {
        await this.$store.dispatch('savedFilters/deleteFilter', this.filterToDelete.id)
        this.deleteDialog = false
        this.filterToDelete = null
      } catch (error) {
        // Error handled by store action
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.v-list-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
}

.theme--dark .v-list-item {
  border-bottom-color: rgba(255, 255, 255, 0.08);

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
}
</style>
